/**
 * Tenant timezone helpers.
 *
 * Bookings are anchored to the tenant's own IANA timezone (stored on
 * `company_info.timezone`) rather than the visiting browser's, so the calendar's
 * past-day boundary and same-day past-slot filtering reflect the business's
 * clock. Existing tenants default to Europe/London.
 */

export const DEFAULT_TIMEZONE = 'Europe/London';

/** A curated list of IANA zones offered in the admin settings UI. */
export const COMMON_TIMEZONES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'Europe/London', label: 'London (UK)' },
  { value: 'Europe/Dublin', label: 'Dublin (Ireland)' },
  { value: 'Europe/Paris', label: 'Paris (France)' },
  { value: 'Europe/Berlin', label: 'Berlin (Germany)' },
  { value: 'Europe/Madrid', label: 'Madrid (Spain)' },
  { value: 'Europe/Rome', label: 'Rome (Italy)' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam (Netherlands)' },
  { value: 'Europe/Lisbon', label: 'Lisbon (Portugal)' },
  { value: 'Europe/Athens', label: 'Athens (Greece)' },
  { value: 'Europe/Istanbul', label: 'Istanbul (Türkiye)' },
  { value: 'Europe/Moscow', label: 'Moscow (Russia)' },
  { value: 'America/New_York', label: 'New York (US Eastern)' },
  { value: 'America/Chicago', label: 'Chicago (US Central)' },
  { value: 'America/Denver', label: 'Denver (US Mountain)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (US Pacific)' },
  { value: 'America/Toronto', label: 'Toronto (Canada Eastern)' },
  { value: 'America/Sao_Paulo', label: 'São Paulo (Brazil)' },
  { value: 'Africa/Lagos', label: 'Lagos (Nigeria)' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg (South Africa)' },
  { value: 'Asia/Dubai', label: 'Dubai (UAE)' },
  { value: 'Asia/Kolkata', label: 'Kolkata (India)' },
  { value: 'Asia/Singapore', label: 'Singapore' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong' },
  { value: 'Asia/Tokyo', label: 'Tokyo (Japan)' },
  { value: 'Australia/Sydney', label: 'Sydney (Australia Eastern)' },
  { value: 'Pacific/Auckland', label: 'Auckland (New Zealand)' },
];

/** True if `tz` is an IANA zone the runtime's Intl can resolve. */
export function isValidTimeZone(tz: string | null | undefined): tz is string {
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** Best-effort IANA zone of the current browser, falling back to the default. */
export function detectBrowserTimeZone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isValidTimeZone(tz) ? tz : DEFAULT_TIMEZONE;
  } catch {
    return DEFAULT_TIMEZONE;
  }
}

/** The tenant's "now": today's calendar day (yyyy-MM-dd) and minutes-since-midnight, in `tz`. */
export function nowInTimeZone(tz: string, at: Date = new Date()): { todayStr: string; minutes: number } {
  const zone = isValidTimeZone(tz) ? tz : DEFAULT_TIMEZONE;
  // en-CA renders an ISO-style yyyy-MM-dd date.
  const todayStr = new Intl.DateTimeFormat('en-CA', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(at);
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: zone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(at);
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? '0') % 24;
  const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? '0');
  return { todayStr, minutes: hour * 60 + minute };
}
